import { useMemo } from 'react';
import { toInspectionOnView } from 'presentation/inspection/inspectionModelToInspectionOnView';
import { ProcessObject } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { SiteModel } from 'shared/domain/site/types/model';
import { LevelModel } from 'shared/domain/level/types/model';
import { FieldValueModel } from 'shared/domain/fieldValue/fieldValueModel';
import { CompanyModel } from 'shared/domain/company/types/model';
import { ContractModel } from 'shared/domain/contract/types/model';
import { InspectionOnView } from 'presentation/inspection/inspectionOnView';
import { logError } from 'setup/logger/logError';

export function useTransformInspectionModel(
  inspectionModel: InspectionModel | undefined,
  inspectionTemplate: InspectionTemplateModel | undefined,
  processes: ProcessObject,
  sites: SiteModel[],
  levels: LevelModel[],
  workTypes: FieldValueModel[],
  companies: CompanyModel[],
  contracts: ContractModel[],
  organizationId: string | null
): InspectionOnView | undefined {
  return useMemo(() => {
    if (!inspectionTemplate || !inspectionModel) {
      return undefined;
    }

    const isTemplateMatchingModel = inspectionTemplate._id === inspectionModel.template;
    if (!isTemplateMatchingModel) {
      return undefined;
    }

    const requiredProcessId = inspectionTemplate.process;
    const hasRequiredProcess = requiredProcessId && processes[requiredProcessId];
    if (!hasRequiredProcess) {
      return undefined;
    }

    try {
      return toInspectionOnView(
        processes,
        inspectionTemplate,
        inspectionModel,
        sites,
        levels,
        workTypes,
        companies,
        contracts,
        organizationId
      );
    } catch (error) {
      logError('Failed to transform inspection model to view model', {
        error,
        inspectionModelId: inspectionModel?._id,
        inspectionTemplateId: inspectionTemplate?._id,
      });
      return undefined;
    }
  }, [
    companies,
    contracts,
    inspectionModel,
    inspectionTemplate,
    levels,
    organizationId,
    processes,
    sites,
    workTypes,
  ]);
}
